import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { makeStyles, useTheme } from '@mui/styles'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import lang from '@/lang'
import { ButtonIconSquare } from '@/modules/base/components/buttons'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import IconPlus from '@assets/images/icons/plus.svg'
import IconEmptyState from '@assets/images/emptyStates/emptyState.svg'
import { useNavigate } from 'react-router'

const useStyles = makeStyles(() => ({
  content: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    textAlign: 'center',
    height: 'calc(100% - 63px)',
  },
  contentPositioned: {
    top: '30%',
    height: 'auto',
    bottom: 'auto',
  },
  title: {
    width: 683,
    height: 22,
    fontSize: 22,
    fontWeight: 'normal',
    fontStretch: 'normal',
    fontStyle: 'normal',
    lineHeight: 1,
    letterSpacing: 0.13,
    textAlign: 'center',
    color: '#282828',
    marginTop: 20,
  },
  subtitle: {
    marginTop: 15,
  },
  button: {
    marginTop: 20,
    marginLeft: 'auto',
    marginRight: 'auto',
  },
}))

const ListEmptyState = (props) => {
  const navigate = useNavigate()
  const classes = useStyles()
  const theme = useTheme()
  const {
    buttonDisabled = false,
    title = lang.list.noRowsToShow,
    noSearchResultTitle,
    noFilterResultTitle,
    subtitle,
    visual = IconEmptyState,
    showButton = false,
    buttonLabel,
    buttonLink,
    buttonIcon = IconPlus,
    isOldGrid = false,
    iconWidth = '30vh',
    iconHeight = '30vh',
    iconFill = theme.grey,
    buttonDataQsId = null,
    list = null,
    buttonClick,
    disabled = false,
  } = props

  const onButtonClick = () => {
    buttonLink && navigate(buttonLink)
  }

  // `list` ist optional: eine Liste, die nicht am `lists`-Slice haengt (z. B. „Meine
  // Meldungen", die ihre Daten aus einem eigenen Duck holt), soll diesen Leerzustand
  // trotzdem benutzen koennen. Vorher warf `state.lists[list]` bei fehlendem `list`.
  // Fuer alle bisherigen Aufrufer — die `list` setzen und deren Eintrag existiert —
  // aendert sich nichts: sie lesen dieselben Werte wie zuvor.
  const listState = useSelector(state => (list ? state.lists?.[list] : undefined))
  const { pagination, searchTerm, filterItems } = listState ?? {}
  const noSearchResult = (searchTerm && searchTerm !== '') && pagination?.count === 0
  const hasItems = (filterItems ?? []).some((filter) => filter.count > 0)

  const icon = useMemo(() => {
    if (noSearchResult) {
      return <SVGIcon svg={IconEmptyState} width='30vh' height='30vh' />
    }

    return <SVGIcon svg={visual} width={iconWidth} height={iconHeight} fill={iconFill} />
  }, [noSearchResult])

  const titleLang = useMemo(() => {
    if (noSearchResult) { return noSearchResultTitle }
    if (hasItems && noFilterResultTitle) { return noFilterResultTitle }
    return title
  }, [noSearchResult, hasItems])

  const subTitle = useMemo(() => {
    if (noSearchResult) { return lang.generics.refineSearch }
    if (hasItems) { return lang.generics.refineFilter }
    return subtitle
  }, [])

  return (
    <div className={isOldGrid ? clsx(classes.content, classes.contentPositioned) : classes.content}>
      <div>{icon}</div>
      <span className={classes.title}>{titleLang}</span>
      {!!subtitle && (<span className={classes.subtitle}>{subTitle}</span>)}
      {showButton && !hasItems && !noSearchResult && (
        <ButtonIconSquare
          disabled={buttonDisabled || disabled }
          customClass={classes.button}
          label={buttonLabel}
          icon={buttonIcon}
          onClick={buttonClick || onButtonClick}
          dataQsId={buttonDataQsId}
        />)
      }
    </div>
  )
}

ListEmptyState.propTypes = {
  iconWidth: PropTypes.string,
  iconHeight: PropTypes.string,
  visual: PropTypes.elementType,
  buttonIcon: PropTypes.string,
  buttonLabel: PropTypes.string,
  buttonLink: PropTypes.string,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  iconFill: PropTypes.string,
  isOldGrid: PropTypes.bool,
  showButton: PropTypes.bool,
  buttonDisabled: PropTypes.bool,
  noSearchResultTitle: PropTypes.string,
  noFilterResultTitle: PropTypes.string,
  list: PropTypes.string,
  disabled: PropTypes.bool,
  buttonDataQsId: PropTypes.string,
}

export default ListEmptyState
